/**
 * Google Analytics utility functions for tracking user interactions
 */

/**
 * Track custom events in Google Analytics
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...eventParams,
      app_name: 'KetoMachine',
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Track page views
 * @param {string} pagePath - Path of the page being viewed
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-XXXXXXXXXX', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

/**
 * Track fasting session events
 */
export const trackFastingEvents = {
  start: (protocolId, targetHours) => {
    trackEvent('fasting_started', {
      protocol: protocolId,
      target_hours: targetHours,
      event_category: 'fasting',
      event_label: `${protocolId} - ${targetHours}h`
    });
  },
  
  complete: (duration, target, protocol) => {
    trackEvent('fasting_completed', {
      duration_hours: duration,
      target_hours: target,
      protocol: protocol,
      success: duration >= target,
      event_category: 'fasting',
      event_label: `Completed ${duration.toFixed(1)}h`
    });
  },
  
  cancel: (elapsed, target) => {
    trackEvent('fasting_cancelled', {
      elapsed_hours: elapsed / 3600,
      target_hours: target,
      event_category: 'fasting',
      event_label: `Cancelled at ${(elapsed / 3600).toFixed(1)}h`
    });
  }
};

/**
 * Track body tracking events
 */
export const trackBodyEvents = {
  weightLogged: (weight, units) => {
    trackEvent('weight_logged', {
      units: units,
      event_category: 'body_tracking',
      event_label: 'Weight Entry'
    });
  },
  
  measurementsLogged: () => {
    trackEvent('measurements_logged', {
      event_category: 'body_tracking',
      event_label: 'Measurements Entry'
    });
  },
  
  photoAdded: (type) => {
    trackEvent('photo_added', {
      photo_type: type,
      event_category: 'body_tracking',
      event_label: `${type} Photo`
    });
  }
};

/**
 * Track achievement and milestone events
 */
export const trackAchievementEvents = {
  badgeEarned: (badgeId, badgeLabel) => {
    trackEvent('badge_earned', {
      badge_id: badgeId,
      badge_name: badgeLabel,
      event_category: 'achievements',
      event_label: badgeLabel
    });
  },
  
  streakMilestone: (streakDays) => {
    trackEvent('streak_milestone', {
      streak_days: streakDays,
      event_category: 'achievements',
      event_label: `${streakDays} Day Streak`
    });
  },
  
  longestFastRecord: (hours) => {
    trackEvent('longest_fast_record', {
      hours: hours,
      event_category: 'achievements',
      event_label: `${hours.toFixed(1)}h Personal Best`
    });
  }
};

/**
 * Track social sharing events
 */
export const trackSocialEvents = {
  share: (shareType, content) => {
    trackEvent('content_shared', {
      share_type: shareType,
      content_type: content,
      event_category: 'social',
      event_label: `Shared ${content}`
    });
  },
  
  achievementsShared: (itemsShared) => {
    trackEvent('achievements_shared', {
      items_count: itemsShared.length,
      items: itemsShared.join(', '),
      event_category: 'social',
      event_label: 'Achievement Share'
    });
  }
};

/**
 * Track app installation and settings
 */
export const trackAppEvents = {
  installed: () => {
    trackEvent('app_installed', {
      event_category: 'app',
      event_label: 'PWA Installation'
    });
  },
  
  notificationsEnabled: () => {
    trackEvent('notifications_enabled', {
      event_category: 'settings',
      event_label: 'Notifications On'
    });
  },
  
  notificationsDisabled: () => {
    trackEvent('notifications_disabled', {
      event_category: 'settings',
      event_label: 'Notifications Off'
    });
  },
  
  unitsChanged: (newUnits) => {
    trackEvent('units_changed', {
      units: newUnits,
      event_category: 'settings',
      event_label: `Changed to ${newUnits}`
    });
  }
};

/**
 * Track navigation between tabs
 */
export const trackNavigation = (tabName) => {
  trackEvent('tab_navigation', {
    tab_name: tabName,
    event_category: 'navigation',
    event_label: `Viewed ${tabName}`
  });
};

/**
 * Track user engagement with motivational features
 */
export const trackMotivationEvents = {
  quoteViewed: (type) => {
    trackEvent('quote_viewed', {
      quote_type: type,
      event_category: 'engagement',
      event_label: `${type} Quote`
    });
  }
};